import { useState, useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { getArticleBySlug as getSanityArticle } from "../services/sanityQueries";
import { getArticleBySlug as getLocalArticle } from "../blog";
import { normalizeSanityArticle } from "../utils/sanityNormalizer";
import {
  buildArticleJsonLd,
  buildFaqJsonLd,
  buildBreadcrumbJsonLd,
  formatDate,
} from "../utils/blogSeo";
import { useTranslation } from "react-i18next";
import { useArticleLocale } from "../hooks/useArticleLocale";
import { SITE_URL } from "../constants/blog";
import { WA_URL } from "../config/constants";

import TOC                   from "../components/blog/TOC";
import FAQAccordion          from "../components/blog/FAQAccordion";
import ShareButtons          from "../components/blog/ShareButtons";
import RelatedArticles       from "../components/blog/RelatedArticles";
import BlogErrorBoundary     from "../components/blog/BlogErrorBoundary";
import PortableTextRenderer  from "../components/blog/PortableTextRenderer";

import "./BlogPost.css";

/** /blog/:slug — Individual article page. Fetches from Sanity CMS. */
export default function BlogPost() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);
  const { t } = useTranslation("blog");
  const { title, description, body, toc, faq } = useArticleLocale(article);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const controller = new AbortController();

    getSanityArticle(slug)
      .then((raw) => {
        if (controller.signal.aborted) return;
        if (raw) setArticle(normalizeSanityArticle(raw));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        const local = getLocalArticle(slug);
        if (local) setArticle(local);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [slug]);

  if (loading) {
    return (
      <main className="blog-post-page blog-post-loading" aria-busy="true" aria-label="Loading article">
        <div className="container post-skeleton">
          <div className="skeleton skeleton-badge" />
          <div className="skeleton skeleton-title" />
          <div className="skeleton skeleton-subtitle" />
          <div className="skeleton skeleton-img" />
          <div className="skeleton skeleton-body" />
        </div>
      </main>
    );
  }

  if (!article) return <Navigate to="/blog" replace />;

  // ── Article is ready ──────────────────────────────────────────────────────
  const related   = article.relatedArticles || [];
  const articleLd = buildArticleJsonLd(article);
  const faqLd     = article.faq?.length ? buildFaqJsonLd(article.faq) : null;
  const crumbLd   = buildBreadcrumbJsonLd([
    { name: "Home",         path: "/" },
    { name: "Blog",         path: "/blog" },
    { name: article.title,  path: `/blog/${article.slug}` },
  ]);

  // Decide how to render body content
  const isSanity = article._source === "sanity";
  const { Content } = article; // local JSX component (may be undefined for Sanity articles)

  return (
    <BlogErrorBoundary>
      <main className="blog-post-page">
        <Helmet>
          <title>{article.title} — ProNet Real Estate</title>
          <meta name="description"                 content={article.description} />
          <link rel="canonical"                    href={`${SITE_URL}/blog/${article.slug}`} />
          <meta name="keywords"                    content={(article.keywords || []).join(", ")} />
          <meta property="og:title"               content={article.title} />
          <meta property="og:description"         content={article.description} />
          <meta property="og:url"                 content={`${SITE_URL}/blog/${article.slug}`} />
          <meta property="og:type"                content="article" />
          <meta property="og:image"               content={article.featuredImage} />
          <meta property="article:published_time"  content={article.date} />
          <meta property="article:modified_time"   content={article.updatedAt || article.date} />
          <meta name="twitter:card"               content="summary_large_image" />
          <meta name="twitter:title"              content={article.title} />
          <meta name="twitter:description"        content={article.description} />
          <meta name="twitter:image"              content={article.featuredImage} />
          <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
          {faqLd && (
            <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
          )}
          <script type="application/ld+json">{JSON.stringify(crumbLd)}</script>
        </Helmet>

        {/* ── ARTICLE HEADER ───────────────────────────────────────────────── */}
        <header className="post-header">
          <div className="container post-header-inner">

            <nav className="post-breadcrumbs" aria-label="Breadcrumb">
              <ol>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/blog">Blog</Link></li>
                <li aria-current="page">{article.category}</li>
              </ol>
            </nav>

            <div className="post-category-badge" aria-label={`Category: ${article.category}`}>
              {article.category}
            </div>

            <h1 className="post-title">{title}</h1>
            <p className="post-subtitle">{description}</p>

            <div className="post-meta-row">
              <div className="post-author">
                {article.author?.photo && (
                  <img
                    src={article.author.photo}
                    alt={article.author.name}
                    className="post-author-img"
                    width="44"
                    height="44"
                    loading="eager"
                  />
                )}
                <div>
                  <span className="post-author-name">{article.author?.name}</span>
                  <span className="post-author-role">{article.author?.role}</span>
                </div>
              </div>
              <div className="post-meta-details">
                <time dateTime={article.date} className="post-date">
                  {formatDate(article.date)}
                </time>
                {article.updatedAt && article.updatedAt !== article.date && (
                  <span className="post-updated">
                    {t("updated")} {formatDate(article.updatedAt)}
                  </span>
                )}
                <span className="post-reading-time" aria-label={`${article.readingTime} ${t("minRead")}`}>
                  {article.readingTime} {t("minRead")}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* ── FEATURED IMAGE ───────────────────────────────────────────────── */}
        {article.featuredImage && (
          <div className="post-hero-img-wrap" role="img" aria-label={article.featuredImageAlt}>
            <img
              src={article.featuredImage}
              alt={article.featuredImageAlt}
              className="post-hero-img"
              fetchpriority="high"
              loading="eager"
              width="1200"
              height="560"
            />
          </div>
        )}

        {/* ── TWO-COLUMN LAYOUT ─────────────────────────────────────────────── */}
        <div className="container post-layout">

          {/* Sidebar */}
          <aside className="post-sidebar" aria-label="Article navigation">
            {toc?.length > 0 && <TOC items={toc} />}

            <div className="post-sidebar-cta" aria-label="Free consultation">
              <p className="post-sidebar-cta-label">{t("sidebar.ctaLabel")}</p>
              <p className="post-sidebar-cta-text">{t("sidebar.ctaText")}</p>
              <a
                href={WA_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-gold btn-gold--full"
                aria-label="Contact us on WhatsApp"
              >
                <span>{t("sidebar.whatsapp")}</span>
              </a>
              <Link to="/contact" className="post-sidebar-link">
                {t("sidebar.message")}
              </Link>
            </div>
          </aside>

          {/* Article body */}
          <article className="post-article" aria-labelledby="post-article-title">
            <h2 id="post-article-title" className="sr-only">
              {title}
            </h2>

            {/* Sanity: Portable Text; Local: JSX component */}
            {isSanity
              ? <PortableTextRenderer value={body} />
              : Content && <Content />
            }

            <ShareButtons slug={article.slug} title={title} />

            {article.tags?.length > 0 && (
              <nav className="post-tags" aria-label={t("tags")}>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="post-tag"
                    aria-label={`Articles tagged ${tag}`}
                  >
                    {tag}
                  </Link>
                ))}
              </nav>
            )}

            {faq?.length > 0 && <FAQAccordion items={faq} />}

            <section className="post-final-cta" aria-labelledby="post-cta-title">
              <div className="section-eyebrow" aria-hidden="true">
                <span className="eyebrow-line" />
                <span>{t("postCta.eyebrow")}</span>
              </div>
              <h2 id="post-cta-title">
                {t("postCta.title0")} <em>{t("postCta.title1")}</em>
              </h2>
              <p>{t("postCta.sub")}</p>
              <div className="post-final-cta-btns">
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-gold"
                  aria-label="Contact us on WhatsApp"
                >
                  <span>{t("postCta.whatsapp")}</span>
                </a>
                <Link to="/listings" className="btn-outline">
                  {t("postCta.browse")}
                </Link>
              </div>
            </section>

            {related.length > 0 && <RelatedArticles articles={related} />}
          </article>
        </div>

        {/* ── STICKY MOBILE WA ─────────────────────────────────────────────── */}
        <a
          href={WA_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="post-sticky-wa"
          aria-label="Get free property consultation on WhatsApp"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span>{t("sticky")}</span>
        </a>
      </main>
    </BlogErrorBoundary>
  );
}
